import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
// At the top of your file, import the JSON data
import graphDataFile from '../data/code_graph.json';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  ButtonDropdown,
  Toggle,
  Select,
  FormField,
  ColumnLayout,
  Alert
} from '@cloudscape-design/components';
import { color } from 'echarts';



export function CodeGraphTab({ project}) {

  // Use dummy data instead of passed props
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedRelationships, setSelectedRelationships] = useState([
    'implements', 'depends_on', 'calls'
  ]);
  const [selectedNodeTypes, setSelectedNodeTypes] = useState([
    'Class', 'Interface', 'Function'
  ]);
  const [graphLayout, setGraphLayout] = useState({ value: 'force' });

  const [selectedDomains, setSelectedDomains] = useState([
    'API Layer', 'Service Layer', 'Data Layer', 'Unknown'
  ]);

  // Add useEffect to fetch the JSON data
  useEffect(() => {
    try {
      setGraphData(graphDataFile);
      setLoading(false);
    } catch (err) {
      console.error('Error loading graph data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  const identifyDomain = (node) => {
    if (node.name.toLowerCase().includes('controller') || 
        node.name.toLowerCase().includes('resource')) {
      return 'API Layer';
    } else if (node.name.toLowerCase().includes('service') || 
               node.name.toLowerCase().includes('manager')) {
      return 'Service Layer';
    } else if (node.name.toLowerCase().includes('repository') || 
               node.name.toLowerCase().includes('repo') ||
               node.name.toLowerCase().includes('dao') ||
               node.name.toLowerCase().includes('entity')) {
      return 'Data Layer';
    }
    return 'Unknown';
  };

  // Process nodes and links from the raw data
  const processGraphData = () => {
    if (!graphData) return { nodes: [], links: [] };

    const nodes = [];
    const links = [];
    const nodeMap = new Map();
  
    // Helper function to add node if it doesn't exist
    const addNode = (node, category) => {
      const id = `${node.type}_${node.name}`;
      if (!nodeMap.has(id) && selectedNodeTypes.includes(node.type)) {
        nodeMap.set(id, {
          id,
          name: node.name,
          value: 1,
          category: node.type,
          symbolSize: getNodeSize(node.type),
          domain: identifyDomain(node)  // Add domain identification
        });
      }
      return id;
    };
  
    // Process all node types
    Object.entries(graphData.nodes).forEach(([nodeType, nodeList]) => {
      nodeList.forEach(node => {
        addNode({ type: nodeType.slice(0, -1), name: node.name });
      });
    });
  
    // Process relationships
    Object.entries(graphData.relationships).forEach(([relType, relations]) => {
      if (selectedRelationships.includes(relType)) {
        relations.forEach(relation => {
          const sourceId = addNode(relation.from);
          const targetId = addNode(relation.to);
          
          if (sourceId && targetId) {
            links.push({
              source: sourceId,
              target: targetId,
              value: relType,
            });
          }
        });
      }
    });
  
    // Filter nodes by domain
    const filteredNodes = Array.from(nodeMap.values()).filter(node => 
      selectedDomains.includes(node.domain || 'Unknown')
    );
  
    // Filter links to only include connections between visible nodes
    const filteredLinks = links.filter(link =>
      filteredNodes.some(n => n.id === link.source) &&
      filteredNodes.some(n => n.id === link.target)
    );
  
    return {
      nodes: filteredNodes,
      links: filteredLinks
    };
  };

  // Add loading and error states to your render
  if (loading) {
    return <div>Loading graph data...</div>;
  }

  if (error) {
    return <div>Error loading graph data: {error}</div>;
  }

  // Get node size based on type
  const getNodeSize = (type) => {
    switch (type) {
      case 'Class':
        return 20;
      case 'Interface':
        return 15;
      case 'Function':
        return 10;
      default:
        return 8;
    }
  };

  // Configure ECharts options
  const getChartOptions = () => {
    const { nodes, links } = processGraphData();

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          if (params.dataType === 'node') {
            return `${params.data.category}: ${params.data.name}`;
          }
          return `${params.data.value}: ${params.data.source} → ${params.data.target}`;
        }
      },
      legend: {
        data: selectedNodeTypes,
        orient: 'vertical',
        left: 'left'
      },
      series: [{
        type: 'graph',
        layout: graphLayout.value,
        animation: true,
        draggable: true,
        data: nodes,
        links: links,
        categories: selectedNodeTypes.map(type => ({ name: type })),
        roam: true,
        label: {
          show: true,
          position: 'right',
          formatter: '{b}'
        },
        force: {
          repulsion: 100,
          edgeLength: 150
        },
        emphasis: {
          focus: 'adjacency',
          lineStyle: {
            width: 10
          }
        },
        lineStyle: {
          color: 'source',
          curveness: 0.3
        },
        // Add hierarchical layout configuration
        layout: graphLayout.value === 'hierarchical' ? 'none' : graphLayout.value,
        ...(graphLayout.value === 'hierarchical' ? {
          orient: 'LR',
          levels: [
            {
              depth: 0,
              itemStyle: {
                color: '#f54545'
              }
            },
            {
              depth: 1,
              itemStyle: {
                color: '#0bb2d4'
              }
            },
            {
              depth: 2,
              itemStyle: {
                color: '#47b262'
              }
            }
          ]
        } : {})
      }]
    };
  };

  // Add domain clustering logic
  const identifyDomains = (nodes, links) => {
    // Create a map to store node relationships
    const relationshipGraph = new Map();
    const domains = new Map();
    const domainColors = {
      'API Layer': '#ff7875',
      'Service Layer': '#95de64',
      'Data Layer': '#69c0ff',
      'Unknown': '#d9d9d9'
    };
  
    // Build relationship graph
    nodes.forEach(node => {
      relationshipGraph.set(node.id, new Set());
    });
  
    links.forEach(link => {
      relationshipGraph.get(link.source)?.add(link.target);
      relationshipGraph.get(link.target)?.add(link.source);
    });
  
    // Identify domains based on connection patterns and naming
    nodes.forEach(node => {
      let domain = 'Unknown';
      
      // Simple domain identification based on naming patterns
      if (node.name.toLowerCase().includes('controller') || 
          node.name.toLowerCase().includes('resource')) {
        domain = 'API Layer';
      } else if (node.name.toLowerCase().includes('service') || 
                 node.name.toLowerCase().includes('manager')) {
        domain = 'Service Layer';
      } else if (node.name.toLowerCase().includes('repository') || 
                 node.name.toLowerCase().includes('dao') ||
                 node.name.toLowerCase().includes('entity')) {
        domain = 'Data Layer';
      }
  
      // Add domain information to node
      node.domain = domain;
      node.itemStyle = {
        color: domainColors[domain]
      };
    });
  
    return domains;
  };
  
  // Add domain colors
  const domainColors = {
    'API Layer': '#ff7875',
    'Service Layer': '#95de64',
    'Data Layer': '#69c0ff',
    'Unknown': '#d9d9d9'
  };
  

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Interactive visualization of code structure and relationships"
            actions={
              <ButtonDropdown
                items={[
                  { text: 'Export as image', id: 'export' },
                  { text: 'Reset zoom', id: 'reset' },
                  { text: 'Fit view', id: 'fit' }
                ]}
              >
                Actions
              </ButtonDropdown>
            }
          >
            Code Graph
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Controls */}
          <ColumnLayout columns={3}>
            <FormField label="Layout">
              <Select
                selectedOption={graphLayout}
                onChange={({ detail }) => setGraphLayout(detail.selectedOption)}
                options={[
                  { label: 'Force directed', value: 'force' },
                  { label: 'Circular', value: 'circular' },
                  { label: 'Hierarchical', value: 'hierarchical' }
                ]}
              />
            </FormField>
            <FormField label="Node Types">
              <SpaceBetween size="xs" direction="horizontal">
                {['Class', 'Interface', 'Function', 'Variable'].map(type => (
                  <Toggle
                    key={type}
                    checked={selectedNodeTypes.includes(type)}
                    onChange={({ detail }) => {
                      setSelectedNodeTypes(prev => 
                        detail.checked 
                          ? [...prev, type]
                          : prev.filter(t => t !== type)
                      );
                    }}
                  >
                    {type}
                  </Toggle>
                ))}
              </SpaceBetween>
            </FormField>
            <FormField label="Relationships">
              <SpaceBetween size="xs" direction="horizontal">
                {['implements', 'depends_on', 'calls', 'declares'].map(rel => (
                  <Toggle
                    key={rel}
                    checked={selectedRelationships.includes(rel)}
                    onChange={({ detail }) => {
                      setSelectedRelationships(prev =>
                        detail.checked
                          ? [...prev, rel]
                          : prev.filter(r => r !== rel)
                      );
                    }}
                  >
                    {rel}
                  </Toggle>
                ))}
              </SpaceBetween>
            </FormField>
            <FormField label="Domains">
              <SpaceBetween size="xs" direction="horizontal">
                {Object.keys(domainColors).map(domain => (
                  <Toggle
                    key={domain}
                    checked={selectedDomains.includes(domain)}
                    onChange={({ detail }) => {
                      setSelectedDomains(prev =>
                        detail.checked
                          ? [...prev, domain]
                          : prev.filter(d => d !== domain)
                      );
                    }}
                  >
                    {domain}
                  </Toggle>
                ))}
              </SpaceBetween>
            </FormField>
          </ColumnLayout>

          {/* Graph */}
          <Box style={{ 
            minHeight: '600px',  // Increased from original
            height: '80vh',      // Changed to viewport height percentage
            width: '100%'        // Ensure full width
          }}>
            <ReactECharts
              option={getChartOptions()}
              style={{ 
                height: '100%',
                width: '100%',
                minHeight: '600px'
              }}
              opts={{ renderer: 'canvas' }}
            />
          </Box>

          {/* Legend */}
          <Alert type="info">
            Drag to move nodes, scroll to zoom, click nodes to highlight connections
          </Alert>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
